import { encode, decode } from '@msgpack/msgpack';

function getHeyGenWebSocketURL() {
  // If you have a .env variable for the HeyGen WS, use it:
  if (process.env.HEYGEN_WS_URL) {
    return process.env.HEYGEN_WS_URL;
  }
  // Otherwise, build from window.location or fallback:
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = protocol === 'wss:' 
    ? 'heygen-service-container-app-bchkhye9hfhheyfe.westus2-01.azurewebsites.net'
    : 'localhost:9000';
  return `${protocol}//${host}/ws`;
}

const HEYGEN_WS_URL = getHeyGenWebSocketURL();
const MAX_RECONNECT_ATTEMPTS = 5;

// Add timestamp utility
const getTimestamp = () => new Date().toISOString();

class HeygenSocketService {
  constructor() {
    this._state = {
      ws: null,
      reconnectAttempts: 0,
      maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
      listeners: new Map(),
      isDisconnecting: false,
      reconnectTimeout: null,
      isConnecting: false,
      connectionPromise: null,
      lastPongTime: Date.now(),
      pingInterval: null,
      wordBuffer: [],              // Buffer words if avatar not ready
      isAvatarReady: false,
      isInCleanup: false,
      connectionCount: 0,
      peerConnection: null,        // WebRTC peer connection
      sessionId: null,             // Current HeyGen session ID
      sessionState: 'disconnected', // disconnected|connecting|started|ready
      currentMediaStream: null     // Store the current media stream
    };
  }

  async setupWebRTC(sdpOffer, iceServers) {
    try {
      // Validate ICE servers configuration
      if (!Array.isArray(iceServers)) {
        console.warn(`[HeyGenWS:${getTimestamp()}] Invalid ICE servers format, using empty array:`, iceServers);
        iceServers = [];
      }

      // Validate each ICE server entry
      iceServers = iceServers.filter(server => {
        if (!server || !server.urls) {
          console.warn(`[HeyGenWS:${getTimestamp()}] Skipping invalid ICE server entry:`, server);
          return false;
        }
        return true;
      });

      console.log(`[HeyGenWS:${getTimestamp()}] Using ICE servers:`, iceServers);

      // Create and configure RTCPeerConnection
      const configuration = { iceServers };
      this._state.peerConnection = new RTCPeerConnection(configuration);

      // Log ICE connection state changes
      this._state.peerConnection.oniceconnectionstatechange = () => {
        console.log(`[HeyGenWS:${getTimestamp()}] ICE connection state:`, this._state.peerConnection.iceConnectionState);
      };

      // Handle media tracks
      this._state.peerConnection.ontrack = (event) => {
        console.log(`[HeyGenWS:${getTimestamp()}] Received media track:`, {
          kind: event.track.kind,
          id: event.track.id,
          label: event.track.label,
          enabled: event.track.enabled,
          readyState: event.track.readyState,
          muted: event.track.muted,
          streamCount: event.streams.length
        });

        if (event.streams.length === 0) {
          console.error(`[HeyGenWS:${getTimestamp()}] No media streams received with track`);
          return;
        }

        const stream = event.streams[0];
        console.log(`[HeyGenWS:${getTimestamp()}] Stream details:`, {
          id: stream.id,
          active: stream.active,
          audioTracks: stream.getAudioTracks().length,
          videoTracks: stream.getVideoTracks().length
        });

        // Store the media stream and notify listeners only if we have video
        if (stream.getVideoTracks().length > 0) {
          this._state.currentMediaStream = stream;
          
          // Add track ended handlers
          stream.getVideoTracks().forEach(track => {
            track.onended = () => {
              console.log(`[HeyGenWS:${getTimestamp()}] Video track ended:`, track.id);
            };
            track.onmute = () => {
              console.log(`[HeyGenWS:${getTimestamp()}] Video track muted:`, track.id);
            };
            track.onunmute = () => {
              console.log(`[HeyGenWS:${getTimestamp()}] Video track unmuted:`, track.id);
            };
          });

          console.log(`[HeyGenWS:${getTimestamp()}] Notifying listeners with media stream containing video`);
          this.notifyListeners('media_stream', stream);
        } else {
          console.warn(`[HeyGenWS:${getTimestamp()}] Stream received but no video tracks found`);
        }
      };

      // Handle ICE candidates
      this._state.peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          console.log(`[HeyGenWS:${getTimestamp()}] New ICE candidate:`, event.candidate);
          this.sendMessage({
            type: 'ice_candidate',
            candidate: {
              candidate: event.candidate.candidate,
              sdpMid: event.candidate.sdpMid,
              sdpMLineIndex: event.candidate.sdpMLineIndex,
              usernameFragment: event.candidate.usernameFragment
            }
          });
        }
      };

      // Handle connection state changes
      this._state.peerConnection.onconnectionstatechange = () => {
        const state = this._state.peerConnection.connectionState;
        console.log(`[HeyGenWS:${getTimestamp()}] WebRTC connection state:`, state);
        
        if (state === 'connected') {
          // Connection established, mark session as ready
          console.log(`[HeyGenWS:${getTimestamp()}] WebRTC connection established, marking session as ready`);
          this.sendMessage({ type: 'session_ready' });
        } else if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          console.error(`[HeyGenWS:${getTimestamp()}] WebRTC connection ${state}`);
          // Could trigger reconnect logic here if needed
        }
      };

      // Validate and set remote description (the offer from HeyGen)
      if (!sdpOffer) {
        throw new Error('No SDP offer provided');
      }

      // Ensure SDP is a string and starts with "v="
      if (typeof sdpOffer !== 'string') {
        console.error(`[HeyGenWS:${getTimestamp()}] Invalid SDP format, expected string but got:`, typeof sdpOffer);
        throw new Error('Invalid SDP format: not a string');
      }

      if (!sdpOffer.trim().startsWith('v=')) {
        console.error(`[HeyGenWS:${getTimestamp()}] Invalid SDP format, does not start with v=:`, sdpOffer);
        throw new Error('Invalid SDP format: does not start with v=');
      }
      
      console.log(`[HeyGenWS:${getTimestamp()}] Setting remote description (offer)`);
      const offer = new RTCSessionDescription({ type: 'offer', sdp: sdpOffer });
      await this._state.peerConnection.setRemoteDescription(offer);

      // Create and set local description (the answer)
      console.log(`[HeyGenWS:${getTimestamp()}] Creating answer`);
      const answer = await this._state.peerConnection.createAnswer();
      console.log(`[HeyGenWS:${getTimestamp()}] Setting local description (answer)`);
      await this._state.peerConnection.setLocalDescription(answer);

      // Send the answer to HeyGen
      console.log(`[HeyGenWS:${getTimestamp()}] Sending session start with answer`);
      this.sendMessage({
        type: 'start_session',
        sdp: answer.sdp
      });

    } catch (error) {
      console.error(`[HeyGenWS:${getTimestamp()}] WebRTC setup error:`, error);
      throw error;
    }
  }

  connect() {
    if (this._state.ws?.readyState === WebSocket.OPEN || 
        (this._state.isConnecting && this._state.connectionPromise)) {
      console.log(`[HeyGenWS:${getTimestamp()}] Connection already exists or in progress`);
      return this._state.connectionPromise || Promise.resolve();
    }

    if (this._state.connectionPromise) {
      console.log(`[HeyGenWS:${getTimestamp()}] Returning existing connection promise`);
      return this._state.connectionPromise;
    }

    this.cleanup();

    this._state.isConnecting = true;
    this._state.connectionPromise = new Promise((resolve, reject) => {
      try {
        console.log(`[HeyGenWS:${getTimestamp()}] Attempting WebSocket connection to ${HEYGEN_WS_URL}`);
        this._state.ws = new WebSocket(HEYGEN_WS_URL);
        
        // Add protocol-level ping handler
        this._state.ws.onping = (event) => {
          console.log(`[HeyGenWS:${getTimestamp()}] Received protocol-level ping`);
          if (this._state.ws?.readyState === WebSocket.OPEN) {
            this._state.ws.pong();
            this._state.lastPongTime = Date.now();
          }
        };
        
        this._state.ws.onopen = () => {
          this._state.connectionCount++;
          console.log(`[HeyGenWS:${getTimestamp()}] WebSocket connected (connection #${this._state.connectionCount})`);
          this._state.reconnectAttempts = 0;
          this._state.isConnecting = false;
          this._state.lastPongTime = Date.now();
          this.setupPingPong();
          resolve();
        };

        this._state.ws.onclose = (event) => {
          const closeTime = getTimestamp();
          this._state.isConnecting = false;
          this._state.connectionPromise = null;
          this._state.sessionState = 'disconnected';
          console.log(`[HeyGenWS:${closeTime}] WebSocket closed with code ${event.code}, clean: ${event.wasClean}, reason: ${event.reason || 'none'}`);
          
          if (!this._state.isDisconnecting && !this._state.isInCleanup) {
            console.log(`[HeyGenWS:${closeTime}] Unexpected closure - attempting reconnect`);
            this.attemptReconnect();
          }
          this.notifyListeners('close', event);
        };

        this._state.ws.onerror = (error) => {
          console.error(`[HeyGenWS:${getTimestamp()}] WebSocket error:`, error);
          this.notifyListeners('error', error);
          if (this._state.isConnecting) {
            this._state.connectionPromise = null;
            reject(error);
          }
        };

        this._state.ws.onmessage = (event) => {
          const receiveTime = getTimestamp();
          console.log(`[HeyGenWS:${receiveTime}] Received message`);
          this.handleMessage(event.data);
        };

      } catch (error) {
        const errorTime = getTimestamp();
        this._state.isConnecting = false;
        this._state.connectionPromise = null;
        console.error(`[HeyGenWS:${errorTime}] Connection error:`, error);
        reject(error);
      }
    });

    return this._state.connectionPromise;
  }

  attemptReconnect() {
    if (this._state.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error('[HeyGenWS] Max reconnect attempts reached');
      return;
    }
    this._state.reconnectAttempts++;
    const backoffDelay = Math.min(1000 * 2 ** (this._state.reconnectAttempts - 1), 10000);
    console.log(`[HeyGenWS] Reconnect attempt #${this._state.reconnectAttempts} in ${backoffDelay}ms`);

    this._state.reconnectTimeout = setTimeout(() => {
      this.connect().catch((err) => {
        console.error('[HeyGenWS] Reconnect failed:', err);
      });
    }, backoffDelay);
  }

  handleMessage(data) {
    let message;
    try {
      if (data instanceof Blob) {
        data.arrayBuffer().then((buffer) => {
          message = decode(new Uint8Array(buffer));
          this.processMessage(message);
        });
      } else {
        message = decode(data);
        this.processMessage(message);
      }
    } catch (err) {
      console.error('[HeyGenWS] Failed to decode message:', err);
    }
  }

  processMessage(msg) {
    const processTime = getTimestamp();
    if (msg.type === 'pong') {
      this._state.lastPongTime = Date.now();
      console.log(`[HeyGenWS:${processTime}] Received pong, updated lastPongTime to ${this._state.lastPongTime}`);
    } else if (msg.type === 'session_created') {
      console.log(`[HeyGenWS:${processTime}] Received session creation info`);
      this._state.sessionId = msg.session_id;
      this._state.sessionState = 'connecting';
      this.setupWebRTC(msg.sdp, msg.ice_servers).catch(error => {
        console.error(`[HeyGenWS:${processTime}] WebRTC setup failed:`, error);
      });
    } else if (msg.type === 'error') {
      console.error(`[HeyGenWS:${processTime}] Received error:`, msg);
      if (msg.code === 'quota_not_enough') {
        this.notifyListeners('error', {
          type: 'quota_exceeded',
          message: 'Your HeyGen API quota has been exhausted. Please check your subscription.'
        });
        // Clean up the session since we can't proceed
        this.cleanup();
      } else {
        this.notifyListeners('error', msg);
      }
    } else if (msg.type === 'session_started') {
      console.log(`[HeyGenWS:${processTime}] Session started successfully`);
      this._state.sessionState = 'started';
    } else if (msg.type === 'ready_confirmed') {
      console.log(`[HeyGenWS:${processTime}] Session is ready for speech tasks`);
      this._state.sessionState = 'ready';
      this._state.isAvatarReady = true;
      this.flushWordBuffer();
      this.notifyListeners('ready', null);
    } else if (msg.type === 'speaking_done') {
      console.log(`[HeyGenWS:${processTime}] Finished speaking word:`, msg.word);
      this.notifyListeners('message', msg);
    } else {
      console.log(`[HeyGenWS:${processTime}] Received message:`, msg);
      this.notifyListeners('message', msg);
    }
  }

  flushWordBuffer() {
    if (!this._state.isAvatarReady) return;
    while (this._state.wordBuffer.length > 0) {
      const nextWord = this._state.wordBuffer.shift();
      this.sendWordToSpeak(nextWord);
    }
  }

  speakWord(word) {
    if (!word) return;
    if (this._state.sessionState !== 'ready') {
      this._state.wordBuffer.push(word);
      console.log(`[HeyGenWS:${getTimestamp()}] Buffered word until session is ready:`, word);
    } else {
      this.sendWordToSpeak(word);
    }
  }

  sendWordToSpeak(word) {
    this.sendMessage({ type: 'speak', word });
  }

  sendMessage(obj) {
    if (this._state.ws && this._state.ws.readyState === WebSocket.OPEN) {
      const encoded = encode(obj);
      this._state.ws.send(encoded);
    }
  }

  setupPingPong() {
    if (this._state.pingInterval) {
      clearInterval(this._state.pingInterval);
    }

    this._state.pingInterval = setInterval(() => {
      if (this._state.ws?.readyState === WebSocket.OPEN) {
        const timeSinceLastPong = Date.now() - this._state.lastPongTime;
        if (timeSinceLastPong > 45000) {
          console.log(`[HeyGenWS:${getTimestamp()}] No pong received in ${timeSinceLastPong}ms, reconnecting`);
          if (this._state.ws) {
            this._state.ws.close();
          }
          return;
        }
        const currentTime = Date.now();
        console.log(`[HeyGenWS:${getTimestamp()}] Sending ping (last pong was ${timeSinceLastPong}ms ago)`);
        this.sendMessage({ type: 'ping', timestamp: currentTime });
      }
    }, 15000);
  }

  disconnect() {
    console.log(`[HeyGenWS:${getTimestamp()}] Disconnecting...`);
    this._state.isDisconnecting = true;
    this.cleanup();
  }

  cleanup() {
    const cleanupTime = getTimestamp();
    console.log(`[HeyGenWS:${cleanupTime}] Starting cleanup`);
    
    this._state.isInCleanup = true;
    
    if (this._state.reconnectTimeout) {
      clearTimeout(this._state.reconnectTimeout);
      this._state.reconnectTimeout = null;
    }

    if (this._state.pingInterval) {
      clearInterval(this._state.pingInterval);
      this._state.pingInterval = null;
    }

    if (this._state.peerConnection) {
      this._state.peerConnection.close();
      this._state.peerConnection = null;
    }

    if (this._state.ws) {
      console.log(`[HeyGenWS:${cleanupTime}] Closing existing connection`);
      const ws = this._state.ws;
      this._state.ws = null;
      
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.onopen = null;
      
      try {
        ws.close(1000);
      } catch (error) {
        console.warn(`[HeyGenWS:${cleanupTime}] Error closing:`, error);
      }
    }

    this._state.isDisconnecting = false;
    this._state.isConnecting = false;
    this._state.connectionPromise = null;
    this._state.isAvatarReady = false;
    this._state.wordBuffer = [];
    this._state.sessionId = null;
    this._state.sessionState = 'disconnected';
    this._state.isInCleanup = false;
  }

  addListener(eventType, callback) {
    if (!this._state.listeners.has(eventType)) {
      this._state.listeners.set(eventType, new Set());
    }
    this._state.listeners.get(eventType).add(callback);
  }

  removeListener(eventType, callback) {
    if (this._state.listeners.has(eventType)) {
      this._state.listeners.get(eventType).delete(callback);
    }
  }

  notifyListeners(eventType, data) {
    if (this._state.listeners.has(eventType)) {
      for (const cb of this._state.listeners.get(eventType)) {
        cb(data);
      }
    }
  }
}

// Create and export the instance
const heygenSocketService = new HeygenSocketService();
export default heygenSocketService; 